import { createFileRoute } from "@tanstack/react-router";
import { serviceSchemaScripts } from "@/lib/service-schema";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";

const PUBLISHED = "2026-09-20T00:00:00+00:00";
const MODIFIED = "2026-09-20T00:00:00+00:00";

const TITLE = "LinkedIn Advertising: Managed B2B Campaigns | Square Migration";
const DESCRIPTION =
  "Managed LinkedIn advertising campaigns for B2B, audience targeting, creative, lead gen forms, budget pacing and lead handoff, run for you from launch to reporting.";

const FAQS: FaqItem[] = [
  {
    question: "What does managed LinkedIn advertising include?",
    answer:
      "We handle the full campaign: audience targeting, campaign structure, creative and copy, lead gen forms, budget pacing, weekly optimization and reporting with lead handoff to your CRM or inbox."
  },
  {
    question: "How much should I budget for a LinkedIn campaign?",
    answer:
      "Most B2B campaigns start between $1,500 and $5,000 per month in ad spend, separate from our management fee. We'll recommend a budget based on your audience size, offer and target cost per lead before anything goes live."
  },
  {
    question: "Do you run ads from our account or yours?",
    answer:
      "Yours. Campaigns run inside your LinkedIn Campaign Manager so you own the account history, the data and every lead. We work inside it as your managed partner."
  },
  {
    question: "What ad formats do you use?",
    answer:
      "Static image ads, video ads, conversation and message ads, and document ads. We match the format to the goal: awareness, engagement or qualified lead capture."
  },
  {
    question: "Where do the leads go?",
    answer:
      "Leads from forms and landing pages are routed to your CRM or inbox with campaign context attached, so sales follows up the same day knowing exactly which ad and audience produced them."
  },
  {
    question: "Do I need a lead magnet or tool before advertising?",
    answer:
      "It helps a lot. LinkedIn ads perform best when they send buyers to something useful, a calculator, assessment or report. We can build that tool first, then advertise it, as a combined engagement."
  },
];

const OFFERINGS = [
  {
    title: "Targeting & Audiences",
    body: "Reach the exact buyers who matter, by job title, seniority, industry, company size, or a named account list.",
    points: ["Job title & seniority filters", "Named-account (ABM) lists", "Website retargeting audiences"],
  },
  {
    title: "Creative & Campaign Build",
    body: "Ads written and designed for a B2B feed, structured into campaigns that can be measured and optimized from day one.",
    points: ["Sponsored content & thought leader ads", "Lead gen forms", "Campaign structure & tracking"],
  },
  {
    title: "Optimization & Reporting",
    body: "Weekly pacing and creative rotation, with a clear monthly read on spend, leads and cost per qualified lead.",
    points: ["Budget pacing & A/B tests", "Monthly performance report", "Lead handoff to CRM or inbox"],
  },
];

export const Route = createFileRoute("/linkedin-advertising")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/linkedin-advertising" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: "https://www.squaremigration.com/linkedin-advertising" }],
    scripts: [...serviceSchemaScripts({
      path: "/linkedin-advertising",
      name: "LinkedIn Advertising",
      description: DESCRIPTION,
      serviceType: "Managed LinkedIn advertising campaigns",
    }), faqSchemaScript(FAQS), webPageSchemaScript({
      path: "/linkedin-advertising",
      name: TITLE,
      description: DESCRIPTION,
      published: PUBLISHED,
      modified: MODIFIED,
    })],
  }),
  component: LinkedInAdvertisingPage,
});

function LinkedInAdvertisingPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="LinkedIn Advertising"
        title="Managed LinkedIn campaigns that put your offer in front of the right buyers."
        description="We run your LinkedIn advertising end to end: audience targeting, creative, lead gen forms, budget pacing and reporting. Campaigns live in your Campaign Manager account, so you own the data and every lead."
        bullets={["Audience & ABM targeting", "Sponsored content", "Lead gen forms", "Weekly optimization", "Lead handoff"]}
      />

      <section
        id="linkedin-advertising"
        className="px-6 md:px-8 lg:px-12 py-16 md:py-20 border-t border-border/50 bg-[#080E1E]/95 text-white"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 lg:gap-20 items-start mb-10 md:mb-14">
            <div>
              <p className="text-xs font-semibold tracking-[0.22em] uppercase text-primary mb-4">
                What We Manage
              </p>
              <h2 className="text-3xl sm:text-4xl font-semibold leading-tight">
                Everything the campaign needs,{" "}
                <span className="text-primary">run for you.</span>
              </h2>
            </div>
            <p className="text-muted-foreground leading-relaxed lg:text-lg lg:pt-11">
              LinkedIn works when the targeting, the creative and the follow-up all point at the same
              buyer. We manage all three, and you keep full ownership of the account, the data and the
              leads it produces.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {OFFERINGS.map((item, i) => (
              <div
                key={item.title}
                className="relative flex flex-col rounded-2xl bg-card/30 border border-border/50 overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-shadow hover:shadow-[0_12px_30px_-16px_rgba(15,23,42,0.25)]"
              >
                <div className="flex items-center justify-between px-6 pt-6 pb-3">
                  <h3 className="text-xl font-semibold">{item.title}</h3>
                  <span className="text-sm font-semibold text-muted-foreground/50 tabular-nums">
                    0{i + 1}
                  </span>
                </div>
                <span className="mx-6 h-1 rounded bg-primary/80" aria-hidden="true" />
                <div className="flex flex-col p-6">
                  <p className="text-muted-foreground leading-relaxed mb-6">{item.body}</p>
                  <div className="mt-auto flex flex-col items-start gap-2 pt-6 border-t border-border/50 sm:flex-row sm:flex-wrap">
                    {item.points.map((point) => (
                      <span
                        key={point}
                        className="inline-flex items-center gap-2 px-2.5 py-1 text-xs font-medium leading-6 text-foreground/90"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                        {point}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap justify-center gap-4">
            <a
              href="https://calendly.com/cgould-mn/new-meeting"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              Review Your Campaign
            </a>
          </div>
        </div>
      </section>

      <ProcessSection
        eyebrow="How a Campaign Runs"
        title="From audience definition to qualified leads in weeks."
        lead="We keep the loop tight: a clearly defined audience, an offer worth the click, and reporting that shows exactly what each lead cost."
        steps={[
          { number: "01", title: "Define the Audience", description: "We translate your ideal buyer into LinkedIn targeting: titles, seniority, industries, company sizes or a named account list." },
          { number: "02", title: "Build the Offer & Creative", description: "Ad copy, creative and lead gen forms built around one clear reason to click, a useful answer, not a sales pitch." },
          { number: "03", title: "Launch & Optimize", description: "Campaigns go live in your Campaign Manager with tracking in place, then we pace budget and rotate creative weekly." },
          { number: "04", title: "Report & Hand Off", description: "You get a clear monthly read on spend, leads and cost per qualified lead, with leads routed to your CRM or inbox with context." },
        ]}
      />

      <ClosingCTA
        eyebrow="Run a Campaign"
        headline={
          <>
            Have something worth putting{" "}
            <span className="text-primary">in front of buyers?</span>
          </>
        }
        subhead="Let's get it in their feed."
        body="Bring your offer, your target buyer, or even just the question your best customers keep asking. We'll tell you in one call what a LinkedIn campaign should look like and what it should cost."
        ctaLabel="Talk Through Your Campaign"
      />

      <FaqSection items={FAQS} />
      <RelatedServices current="/linkedin-advertising" />
      <Footer />
    </main>
  );
}
